import { useState, useEffect, useCallback } from "react";
import api, { isOrganizationScopeCancel, settleOrganizationScopeCancel } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { useOrganizationScope } from "../contexts/OrganizationScopeContext";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Shield,
  ShieldAlert,
  ShieldCheck,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Play,
  Download,
  RefreshCw,
  User,
  Plane,
  Battery,
  FileText,
  Target,
  AlertOctagon,
  ClipboardList,
  Building2,
  Calendar,
  TrendingUp,
  History,
  FileSpreadsheet,
  Upload,
} from "lucide-react";
import { toast } from "sonner";


export const AuditSimulation = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const {
    activeOrganizationId,
    clearScope,
    isPlatformAdmin,
    organizations,
    setActiveOrganization,
  } = useOrganizationScope();
  const selectedOrg = isPlatformAdmin ? activeOrganizationId : user?.organization_id;
  const [auditResult, setAuditResult] = useState(null);
  const [auditHistory, setAuditHistory] = useState([]);
  const [importHistory, setImportHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [runningAudit, setRunningAudit] = useState(false);

  const fetchAuditHistory = useCallback(async () => {
    try {
      const params = selectedOrg ? `?organization_id=${selectedOrg}` : "";
      const response = await api.get(`/audits/history${params}`);
      setAuditHistory(response.data);
    } catch (err) {
      // Silent fail - audit history stays empty
    }
  }, [selectedOrg]);

  const fetchImportHistory = useCallback(async () => {
    try {
      // Fetch completed/failed import jobs for audit log display
      // This uses the existing import jobs endpoint
      const params = new URLSearchParams();
      if (selectedOrg) params.append("organization_id", selectedOrg);
      params.append("status", "completed,failed");
      
      // For now, we'll fetch from import_jobs collection
      // Backend may need an endpoint for this - for now showing placeholder
      setImportHistory([]);
    } catch (err) {
      // Silent fail
    }
  }, [selectedOrg]);

  useEffect(() => {
    setAuditResult(null);
    fetchAuditHistory();
    fetchImportHistory();
  }, [selectedOrg, fetchAuditHistory, fetchImportHistory]);

  const runAudit = async () => {
    if (!selectedOrg) {
      toast.error(t.audit?.selectOrgError || "Please select an organization");
      return;
    }

    setRunningAudit(true);
    try {
      const response = await api.post(
        `/audits/run?organization_id=${selectedOrg}`,
        {},
        settleOrganizationScopeCancel()
      );
      setAuditResult(response.data);
      toast.success((t.audit?.auditComplete || "Audit completed: {id}").replace("{id}", response.data.audit_id));
      fetchAuditHistory();
    } catch (err) {
      if (isOrganizationScopeCancel(err)) return;
      toast.error(t.audit?.runAuditError || "Failed to run audit");
    } finally {
      setRunningAudit(false);
    }
  };

  const downloadAuditPackage = async (auditId) => {
    try {
      const response = await api.get(`/audits/package/${auditId}`, {
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `audit_package_${auditId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(t.audit?.packageDownloaded || "Audit package downloaded");
    } catch (err) {
      toast.error(t.audit?.downloadError || "Failed to download audit package");
    }
  };

  const loadAuditDetail = async (auditId) => {
    setLoading(true);
    try {
      const response = await api.get(`/audits/history/${auditId}`, settleOrganizationScopeCancel());
      const fullResult = response.data.full_result;
      if (fullResult) {
        setAuditResult({
          ...response.data,
          categories: fullResult.categories,
          summary: fullResult.summary,
        });
      }
    } catch (err) {
      if (isOrganizationScopeCancel(err)) return;
      toast.error(t.audit?.detailLoadError || "Failed to load audit detail");
    } finally {
      setLoading(false);
    }
  };

  const getSeverityBadge = (severity) => {
    const config = {
      pass: { class: "bg-emerald-100 text-emerald-700", icon: CheckCircle, label: "PASS" },
      warning: { class: "bg-amber-100 text-amber-700", icon: AlertTriangle, label: "WARNING" },
      critical: { class: "bg-red-100 text-red-700", icon: XCircle, label: "CRITICAL" },
    };
    const cfg = config[severity] || config.warning;
    const Icon = cfg.icon;
    return (
      <Badge className={`${cfg.class} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {cfg.label}
      </Badge>
    );
  };

  const getReadinessBadge = (status) => {
    if (status === "ready") {
      return (
        <Badge className="bg-emerald-100 text-emerald-700 text-base px-4 py-1">
          <ShieldCheck className="h-4 w-4 mr-2" />
          {t.audit?.ready || "AUDIT READY"}
        </Badge>
      );
    }
    return (
      <Badge className="bg-red-100 text-red-700 text-base px-4 py-1">
        <ShieldAlert className="h-4 w-4 mr-2" />
        {t.audit?.attentionRequired || "ATTENTION REQUIRED"}
      </Badge>
    );
  };

  const getCategoryIcon = (category) => {
    const icons = {
      pilot: User,
      aircraft: Plane,
      battery: Battery,
      document: FileText,
      mission: Target,
      incident: AlertOctagon,
      scenario: ClipboardList,
    };
    return icons[category] || Shield;
  };

  const getCategoryLabel = (category) => {
    const labels = {
      pilot: t.audit?.pilotCertifications || "Pilot Certifications",
      aircraft: t.audit?.aircraftMaintenance || "Aircraft Maintenance",
      battery: t.audit?.batterySafety || "Battery Safety",
      document: t.audit?.documentation || "Documentation",
      mission: t.audit?.missionCompliance || "Mission Compliance",
      incident: t.audit?.incidentStatus || "Incident Status",
      scenario: t.audit?.scenarioAssignments || "Scenario Assignments",
    };
    return labels[category] || category;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString();
  };

  return (
    <div className="p-8 space-y-6" data-testid="audit-simulation-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            {t.audit?.title || "Compliance Audit Simulation"}
          </h1>
          <p className="text-slate-500 mt-1">
            {t.audit?.description || "Simulate regulatory compliance audits and identify operational weaknesses"}
          </p>
        </div>
        <div className="flex items-center gap-4">
          {isPlatformAdmin && organizations.length > 0 && (
            <Select
              value={selectedOrg || "__all__"}
              onValueChange={(value) => value === "__all__" ? clearScope() : setActiveOrganization(value)}
            >
              <SelectTrigger className="w-64">
                <Building2 className="h-4 w-4 mr-2" />
                <SelectValue placeholder={t.common?.selectOrganization || "Select Organization"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">{t.organizationScope?.allOrganizations}</SelectItem>
                {organizations.map((org) => (
                  <SelectItem key={org.id} value={org.id}>
                    {org.company_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button
            onClick={runAudit}
            disabled={runningAudit || !selectedOrg}
            className="flex items-center gap-2"
            data-testid="run-audit-btn"
          >
            {runningAudit ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <Play className="h-4 w-4" />
            )}
            {t.audit?.runAudit || "Run Audit"}
          </Button>
        </div>
      </div>

      <Tabs defaultValue="results" className="space-y-6">
        <TabsList>
          <TabsTrigger value="results" className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            {t.audit?.results || "Audit Results"}
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2">
            <History className="h-4 w-4" />
            {t.audit?.history || "Audit History"}
          </TabsTrigger>
          <TabsTrigger value="imports" className="flex items-center gap-2">
            <FileSpreadsheet className="h-4 w-4" />
            {t.audit?.filterCsvImports || "CSV Imports"}
          </TabsTrigger>
        </TabsList>

        {/* Audit Results Tab */}
        <TabsContent value="results" className="space-y-6">
          {auditResult ? (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="border-l-4 border-l-blue-500">
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">{t.audit?.complianceScore || "Compliance Score"}</p>
                        <p className={`text-3xl font-bold ${
                          auditResult.compliance_score >= 70 ? "text-emerald-600" :
                          auditResult.compliance_score >= 50 ? "text-amber-600" : "text-red-600"
                        }`}>
                          {auditResult.compliance_score}%
                        </p>
                      </div>
                      <TrendingUp className="h-8 w-8 text-blue-200" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-emerald-500">
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">{t.audit?.passed || "Passed"}</p>
                        <p className="text-3xl font-bold text-emerald-600">{auditResult.total_passed}</p>
                      </div>
                      <CheckCircle className="h-8 w-8 text-emerald-200" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-amber-500">
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">{t.audit?.warnings || "Warnings"}</p>
                        <p className="text-3xl font-bold text-amber-600">{auditResult.total_warnings}</p>
                      </div>
                      <AlertTriangle className="h-8 w-8 text-amber-200" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-red-500">
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">{t.audit?.critical || "Critical"}</p>
                        <p className="text-3xl font-bold text-red-600">{auditResult.total_critical}</p>
                      </div>
                      <XCircle className="h-8 w-8 text-red-200" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Audit Readiness Banner */}
              <Card className={`${
                auditResult.audit_readiness === "ready" 
                  ? "bg-emerald-50 border-emerald-200" 
                  : "bg-red-50 border-red-200"
              }`}>
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {getReadinessBadge(auditResult.audit_readiness)}
                      <div>
                        <p className="text-sm text-slate-600">
                          {t.audit?.auditId || "Audit ID"}: <span className="font-mono font-medium">{auditResult.audit_id}</span>
                        </p>
                        <p className="text-sm text-slate-500">
                          {formatDate(auditResult.audit_date)}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      onClick={() => downloadAuditPackage(auditResult.audit_id)}
                      className="flex items-center gap-2"
                      data-testid="download-package-btn"
                    >
                      <Download className="h-4 w-4" />
                      {t.audit?.downloadPackage || "Download Audit Package"}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Category Findings */}
              <Card>
                <CardHeader>
                  <CardTitle>{t.audit?.findingsByCategory || "Findings by Category"}</CardTitle>
                  <CardDescription>
                    {t.audit?.findingsDescription || "Detailed breakdown of compliance issues found during the audit"}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Accordion type="multiple" className="w-full">
                    {auditResult.categories && Object.entries(auditResult.categories).map(([category, data]) => {
                      const Icon = getCategoryIcon(category);
                      const hasIssues = data.warnings > 0 || data.critical > 0;
                      
                      return (
                        <AccordionItem key={category} value={category}>
                          <AccordionTrigger className="hover:no-underline">
                            <div className="flex items-center justify-between w-full pr-4">
                              <div className="flex items-center gap-3">
                                <Icon className="h-5 w-5 text-slate-500" />
                                <span className="font-medium">{getCategoryLabel(category)}</span>
                              </div>
                              <div className="flex items-center gap-4">
                                <span className="text-sm text-slate-500">
                                  {data.total_checked} {t.audit?.checked || "checked"}
                                </span>
                                {data.critical > 0 && (
                                  <Badge className="bg-red-100 text-red-700">{data.critical} Critical</Badge>
                                )}
                                {data.warnings > 0 && (
                                  <Badge className="bg-amber-100 text-amber-700">{data.warnings} Warning</Badge>
                                )}
                                {!hasIssues && (
                                  <Badge className="bg-emerald-100 text-emerald-700">
                                    <CheckCircle className="h-3 w-3 mr-1" />
                                    All Passed
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </AccordionTrigger>
                          <AccordionContent>
                            {data.findings && data.findings.length > 0 ? (
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead>{t.audit?.entity || "Entity"}</TableHead>
                                    <TableHead>{t.audit?.severity || "Severity"}</TableHead>
                                    <TableHead>{t.audit?.issue || "Issue"}</TableHead>
                                    <TableHead>{t.audit?.suggestedAction || "Suggested Action"}</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {data.findings.map((finding, idx) => (
                                    <TableRow key={`finding-${finding.entity_name?.substring(0, 10)}-${finding.severity}-${idx}`}>
                                      <TableCell className="font-medium">{finding.entity_name}</TableCell>
                                      <TableCell>{getSeverityBadge(finding.severity)}</TableCell>
                                      <TableCell className="max-w-xs">{finding.issue_description}</TableCell>
                                      <TableCell className="max-w-xs text-slate-600">{finding.suggested_action}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            ) : (
                              <div className="text-center py-4 text-slate-500">
                                <CheckCircle className="h-8 w-8 mx-auto mb-2 text-emerald-400" />
                                <p>{t.audit?.noIssues || "No issues found in this category"}</p>
                              </div>
                            )}
                          </AccordionContent>
                        </AccordionItem>
                      );
                    })}
                  </Accordion>
                </CardContent>
              </Card>
            </>
          ) : (
            <Card className="bg-slate-50">
              <CardContent className="pt-8 pb-8">
                <div className="text-center">
                  <Shield className="h-16 w-16 mx-auto mb-4 text-slate-300" />
                  <h3 className="text-lg font-medium text-slate-700 mb-2">
                    {t.audit?.noAuditRun || "No Audit Run Yet"}
                  </h3>
                  <p className="text-slate-500 mb-4">
                    {t.audit?.clickToRun || "Click 'Run Audit' to simulate a compliance audit for the selected organization"}
                  </p>
                  <Button onClick={runAudit} disabled={runningAudit || !selectedOrg}>
                    <Play className="h-4 w-4 mr-2" />
                    {t.audit?.runAudit || "Run Audit"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Audit History Tab */}
        <TabsContent value="history" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="h-5 w-5" />
                {t.audit?.auditHistory || "Audit History"}
              </CardTitle>
              <CardDescription>
                {t.audit?.historyDescription || "View past audit runs and download audit packages"}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {auditHistory.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t.audit?.auditId || "Audit ID"}</TableHead>
                      <TableHead>{t.common?.organization || "Organization"}</TableHead>
                      <TableHead>{t.common?.date || "Date"}</TableHead>
                      <TableHead>{t.audit?.score || "Score"}</TableHead>
                      <TableHead>{t.audit?.status || "Status"}</TableHead>
                      <TableHead>{t.audit?.issues || "Issues"}</TableHead>
                      <TableHead>{t.common?.actions || "Actions"}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {auditHistory.map((audit) => (
                      <TableRow key={audit.id}>
                        <TableCell className="font-mono text-sm">{audit.audit_id}</TableCell>
                        <TableCell>{audit.organization_name}</TableCell>
                        <TableCell>{formatDate(audit.audit_date)}</TableCell>
                        <TableCell>
                          <span className={`font-bold ${
                            audit.compliance_score >= 70 ? "text-emerald-600" :
                            audit.compliance_score >= 50 ? "text-amber-600" : "text-red-600"
                          }`}>
                            {audit.compliance_score}%
                          </span>
                        </TableCell>
                        <TableCell>
                          {audit.audit_readiness === "ready" ? (
                            <Badge className="bg-emerald-100 text-emerald-700">Ready</Badge>
                          ) : (
                            <Badge className="bg-red-100 text-red-700">Attention</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            {audit.total_critical > 0 && (
                              <Badge className="bg-red-100 text-red-700">{audit.total_critical} Critical</Badge>
                            )}
                            {audit.total_warnings > 0 && (
                              <Badge className="bg-amber-100 text-amber-700">{audit.total_warnings} Warning</Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => loadAuditDetail(audit.audit_id)}
                            >
                              {t.common?.view || "View"}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => downloadAuditPackage(audit.audit_id)}
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <History className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                  <p>{t.audit?.noHistory || "No audit history found"}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* CSV Imports Tab */}
        <TabsContent value="imports" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileSpreadsheet className="h-5 w-5" />
                {t.audit?.filterCsvImports || "CSV Imports"}
              </CardTitle>
              <CardDescription>
                {t.audit?.historyDescription?.replace("audit", "import") || "View past import operations"}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {importHistory.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t.common?.date || "Date"}</TableHead>
                      <TableHead>{t.common?.organization || "Organization"}</TableHead>
                      <TableHead>{t.audit?.actionCsvImport || "Entity"}</TableHead>
                      <TableHead>{t.audit?.sourceFile || "Source File"}</TableHead>
                      <TableHead>{t.audit?.importedRecords || "Imported"}</TableHead>
                      <TableHead>{t.audit?.skippedRecords || "Skipped"}</TableHead>
                      <TableHead>{t.audit?.failedRecords || "Failed"}</TableHead>
                      <TableHead>{t.audit?.status || "Status"}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {importHistory.map((importJob) => (
                      <TableRow key={importJob.job_id || importJob.id}>
                        <TableCell>{formatDate(importJob.completed_at || importJob.started_at)}</TableCell>
                        <TableCell>{importJob.organization_name || "-"}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">
                            {importJob.entity_type}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-slate-600">
                          {importJob.file_name || "-"}
                        </TableCell>
                        <TableCell>
                          <span className="text-emerald-600 font-medium">{importJob.imported || 0}</span>
                        </TableCell>
                        <TableCell>
                          <span className="text-amber-600">{importJob.skipped || 0}</span>
                        </TableCell>
                        <TableCell>
                          <span className="text-red-600">{importJob.failed || 0}</span>
                        </TableCell>
                        <TableCell>
                          {importJob.status === "completed" ? (
                            <Badge className="bg-emerald-100 text-emerald-700">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              {t.common?.success || "Success"}
                            </Badge>
                          ) : importJob.status === "failed" ? (
                            <Badge className="bg-red-100 text-red-700">
                              <XCircle className="h-3 w-3 mr-1" />
                              {t.import?.resultFailed || "Failed"}
                            </Badge>
                          ) : (
                            <Badge className="bg-amber-100 text-amber-700">
                              {importJob.status}
                            </Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Upload className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                  <p>{t.audit?.noHistory || "No import history found"}</p>
                  <p className="text-sm mt-2">
                    {t.import?.dropzoneSubtitle || "Import data from the entity pages (Pilots, Aircraft, Batteries, Documents)"}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AuditSimulation;
